import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AlbumCard } from "@/components/cards/AlbumCard";
import { CoverImg } from "@/components/ui/CoverImg";
import { SectionShelf } from "@/components/shelf/SectionShelf";
import { Skeleton } from "@/components/ui/Skeleton";
import { TrackRow } from "@/components/tracks/TrackRow";
import { useLibrary } from "@/hooks/useLibrary";
import { usePreloadImage } from "@/hooks/usePreloadImage";
import { coverUrl } from "@/lib/cover";
import { musicService } from "@/services/musicService";
import type { Album, Artist, Track } from "@/types/types";

const TOP_TRACKS_SHOWN = 5;

export function ArtistPage() {
  const { id = "" } = useParams();
  const [artist, setArtist] = useState<Artist | null>(null);
  const [albums, setAlbums] = useState<Album[]>([]);
  const [topTracks, setTopTracks] = useState<Track[]>([]);
  const [loadingTop, setLoadingTop] = useState(true);
  const [loadingAlbums, setLoadingAlbums] = useState(true);
  const { isLiked, toggle } = useLibrary();
  usePreloadImage(artist?.cover_id ? coverUrl(artist.cover_id, 232) : null);

  useEffect(() => {
    setLoadingTop(true);
    setLoadingAlbums(true);
    setAlbums([]);
    setTopTracks([]);
    musicService.artist(id).then(setArtist).catch(console.error);
    musicService.artistTopTracks(id)
      .then((tracks) => setTopTracks(tracks || []))
      .catch((e) => { console.warn("top tracks failed", e); setTopTracks([]); })
      .finally(() => setLoadingTop(false));
    musicService.artistAlbums(id)
      .then((p) => setAlbums(p?.items || []))
      .catch((e) => { console.warn("albums failed", e); setAlbums([]); })
      .finally(() => setLoadingAlbums(false));
  }, [id]);

  if (!artist) return <Skeleton height={220} />;
  const liked = isLiked("artist", artist.id);
  const shownTop = topTracks.slice(0, TOP_TRACKS_SHOWN);

  return (
    <div>
      <header className="hero" style={{
        display: "flex", flexDirection: "row", alignItems: "flex-end",
        gap: 28, marginBottom: 28,
      }}>
        <CoverImg coverId={artist.cover_id} alt={artist.name} size={232} priority />
        <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingBottom: 6 }}>
          <div className="muted" style={{ fontSize: 13 }}>Artiste</div>
          <h1 style={{ margin: 0, fontSize: 56, lineHeight: 1.05, fontWeight: 800 }}>
            {artist.name}
          </h1>
          <div>
            <button className="btn" onClick={() => void toggle("artist", artist.id)}
                    style={{ marginTop: 8 }}>
              {liked ? "♥ Suivi" : "Suivre"}
            </button>
          </div>
        </div>
      </header>

      {loadingTop ? (
        <section style={{ marginBottom: 32 }}>
          <h2 style={{ margin: "0 0 12px", fontSize: 22 }}>Populaires</h2>
          {Array.from({ length: 3 }).map((_, i) =>
            <Skeleton key={i} height={44} style={{ marginBottom: 6 }} />
          )}
        </section>
      ) : shownTop.length > 0 && (
        <section style={{ marginBottom: 32 }}>
          <h2 style={{ margin: "0 0 12px", fontSize: 22 }}>Populaires</h2>
          <div className="tracklist">
            {shownTop.map((t, i) => (
              <TrackRow key={t.id} track={t} index={i} queue={shownTop} />
            ))}
          </div>
        </section>
      )}

      {loadingAlbums ? (
        <section>
          <h2 style={{ margin: "0 0 12px", fontSize: 22 }}>Discographie</h2>
          <div className="shelf">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={220} />)}
          </div>
        </section>
      ) : albums.length > 0 ? (
        <SectionShelf title="Discographie">
          {albums.map((a) => <AlbumCard key={a.id} album={a} />)}
        </SectionShelf>
      ) : (
        <section>
          <h2 style={{ margin: "0 0 12px", fontSize: 22 }}>Discographie</h2>
          <p className="muted" style={{ margin: 0 }}>
            Aucun album trouvé — Spotify n'a rien renvoyé pour cet artiste.
          </p>
        </section>
      )}
    </div>
  );
}
